/**
 * Listino modificabile dall'amministratore (ADR 0058): client delle rotte di `billing`.
 *
 * `publish` e `apply` portano il codice TOTP: lo verifica il gateway prima di inoltrare
 * (403 `mfa_code_invalid` se sbagliato, 429 dopo troppi tentativi).
 *
 * Provvisorio dichiarato, come gli altri client (CLAUDE.md §2.3): tipi scritti a mano,
 * presidiati da `priceList.test.ts`.
 */

import type { ApiClient } from "./client";

/** Piani del listino. */
export type CatalogPlanId = "free" | "base" | "premium";

/** Prezzo e limiti di un piano. */
export interface PlanEntry {
  price_cents: number;
  /** Canone in centesimi di dollaro (ADR 0064); `null`: dal canone in euro per il cambio. */
  price_cents_usd?: number | null;
  max_sets: number;
  max_projects_per_set: number;
  monthly_token_limit: number;
  monthly_input_tokens?: number | null;
  monthly_output_tokens?: number | null;
  monthly_cache_tokens?: number | null;
  next_monthly_token_limit?: number | null;
  next_from?: string | null;
  monthly_voice_minutes: number;
  max_set_members: number;
  production_sites: boolean;
  max_custom_domains: number;
  hosting_storage_gib: number;
  set_storage_gib: number;
  max_agent_size: "small" | "medium" | "large";
  stripe_lookup_key?: string;
}

/** Prezzi a consumo, in centesimi. */
export interface UsagePrices {
  token_block_cents: number;
  voice_minute_cents: number;
  storage_gib_month_cents: number;
  default_spending_cap_cents: number;
  max_spending_cap_cents: number;
}

/** Costi stimati, solo per il margine. */
export interface Costs {
  token_million_usd: number;
  voice_minute_usd: number;
  storage_gib_month_usd: number;
  eur_usd: number;
  vat_rate: number;
}

/** Una versione del listino. */
/** Prezzi standard delle API di un modello, in dollari per milione di token (ADR 0063). */
export interface ModelPriceRow {
  id: string;
  label: string;
  family: "opus" | "sonnet" | "haiku" | "fable" | "other";
  prefixes: string[];
  input: number;
  output: number;
  cache_write_5m: number;
  cache_write_1h: number;
  cache_read: number;
}

export interface CatalogBody {
  plans: Record<CatalogPlanId, PlanEntry>;
  usage: UsagePrices;
  costs: Costs;
  model_prices?: ModelPriceRow[];
}

/** Una versione salvata. */
export interface CatalogVersion {
  version: number;
  status: "draft" | "published";
  body: CatalogBody;
  note: string;
  created_by: string;
  published_by: string;
  published_at: string | null;
}

/** Stato del listino per la pagina. */
export interface CatalogAdminState {
  active: CatalogVersion;
  draft: CatalogVersion | null;
  editable: CatalogBody;
  subscribers: Partial<Record<CatalogPlanId, number>>;
  notice_days: number;
}

/** Esito di «Applica agli abbonati». */
export interface ApplyResult {
  scheduled: number;
  notice_days: number;
}

/** Base delle rotte. */
const BASE = "/api/v1/billing/admin/catalog";

/** Operazioni sul listino. */
export class PriceListApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /** Versione in vigore, bozza e abbonati. */
  get(): Promise<CatalogAdminState> {
    return this.client.request<CatalogAdminState>("GET", BASE);
  }

  /**
   * Scrive la bozza.
   *
   * @param body Listino completo.
   * @returns Lo stato aggiornato.
   */
  saveDraft(body: CatalogBody): Promise<CatalogAdminState> {
    return this.client.request<CatalogAdminState>("PUT", `${BASE}/draft`, body);
  }

  /** Cancella la bozza. */
  async discardDraft(): Promise<void> {
    await this.client.request<void>("DELETE", `${BASE}/draft`);
  }

  /**
   * Pubblica la bozza.
   *
   * @param note Che cosa cambia e perché.
   * @param code Codice TOTP dell'amministratore.
   * @returns Lo stato aggiornato.
   */
  publish(note: string, code: string): Promise<CatalogAdminState> {
    return this.client.request<CatalogAdminState>("POST", `${BASE}/publish`, {
      note,
      code,
    });
  }

  /**
   * Prezzi nuovi agli abbonati, dal rinnovo dopo il preavviso.
   *
   * @param code Codice TOTP dell'amministratore.
   * @returns Quanti abbonati e con quale preavviso.
   */
  apply(code: string): Promise<ApplyResult> {
    return this.client.request<ApplyResult>("POST", `${BASE}/apply`, { code });
  }

  /** Versioni pubblicate, dalla più recente. */
  versions(): Promise<CatalogVersion[]> {
    return this.client.request<CatalogVersion[]>("GET", `${BASE}/versions`);
  }
}
